import { ContactDialog } from "client/Features/AddressBook/ContactDialog";
import { AddressBookContactSource } from "client/Features/AddressBook/Overview/Overview";
import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	guid,
	newGuid,
	Guid
} from "client_legacy/ndaw/guid";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileTabProps } from "../TileTabSelector";
import { GlobalContactsTab } from "./Tabs/GlobalContactsTab";
import { PersonalContactsTab } from "./Tabs/PersonalContactsTab";
import { WorkspaceContactsTab } from "./Tabs/WorkspaceContactsTab";
import { ContactSource } from "client/Api/Email/contacts";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { ConfigurableAddressBookAppContactSource } from "client/Api/Apps/EditApp/saveApp";
import { isNotNilOrWhiteSpace } from "client/Misc/utils";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";
import { AddressBookViewKind } from "client/Api/Workspace/workspace";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SourceFilter } from "client/Features/AddressBook/SourceSelector";
import { navigateToAddressBookPage } from "client/Features/Layout/GlobalNavigationSidebar/SubNavigationContent/AddressBookPanel";

export const addressBookLiveTilePageSize = 20;

export type AddressBookLiveTileContentProps = {
	liveUpdateInterval: number;
	configurableContactSources: readonly ConfigurableAddressBookAppContactSource[];
	defaultView: AddressBookViewKind;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const AddressBookLiveTileContent = (props: AddressBookLiveTileContentProps): ReactElement => {
	const {
		liveUpdateInterval,
		configurableContactSources,
		defaultView,
		onOpenTileMenu
	} = props;

	const {
		icon,
		id,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const [isNewContactDialogOpen, setIsNewContactDialogOpen] = useState<boolean>(false);

	const [selectedActiveView, setSelectedActiveView] = useState<AddressBookViewKind>(defaultView);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const { department } = userConfiguration().currentUserIdentity;

	const tabConfigurationMap = useMemo(
		(): Record<AddressBookViewKind, TabConfiguration> => {
			return {
				[AddressBookViewKind.Personal]: {
					tabId: guid("178b55ec-dabe-444a-a699-0efb1375e054"),
					contactSource: ContactSource.Personal,
					tabName: localize("_PersonalContacts")
				},
				[AddressBookViewKind.Organization]: {
					tabId: guid("278b55ec-dabe-444a-a699-0efb1375e054"),
					contactSource: ContactSource.Gal,
					tabName: localize("_OrganisationContacts")
				},
				[AddressBookViewKind.Workspace]: {
					tabId: guid("378b55ec-dabe-444a-a699-0efb1375e054"),
					contactSource: ContactSource.Workspace,
					tabName: localize("_WorkspaceContacts")
				},
				[AddressBookViewKind.Department]: {
					tabId: guid("478b55ec-dabe-444a-a699-0efb1375e054"),
					contactSource: ContactSource.Workspace,
					tabName: department ?? ""
				},
			};
		},
		[department]
	);

	const availableViews = useMemo(
		(): readonly AddressBookViewKind[] => {
			return [
				AddressBookViewKind.Workspace,
				...(isNotNilOrWhiteSpace(department) ? [AddressBookViewKind.Department] : []),
				...configurableContactSources.includes(ConfigurableAddressBookAppContactSource.Personal)
					? [AddressBookViewKind.Personal]
					: [],
				...configurableContactSources.includes(ConfigurableAddressBookAppContactSource.Gal)
					? [AddressBookViewKind.Organization]
					: [],
			];
		},
		[configurableContactSources, department]
	);

	const activeView = useMemo(
		(): AddressBookViewKind => {
			return availableViews.includes(selectedActiveView)
				? selectedActiveView
				: availableViews[0];
		},
		[selectedActiveView, availableViews]
	);

	const activeTabConfiguration: TabConfiguration = tabConfigurationMap[activeView];

	const activeContactSource: AddressBookContactSource = activeTabConfiguration.contactSource;

	const activeTabId: Guid = activeTabConfiguration.tabId;

	const tabs: readonly TileTabProps[] = useMemo(
		() => {
			return availableViews.map(
				(x: AddressBookViewKind): TileTabProps => ({
					uniqueId: tabConfigurationMap[x].tabId,
					name: tabConfigurationMap[x].tabName
				})
			);
		},
		[availableViews, tabConfigurationMap]
	);

	const searchTermChangeHandler = useCallback(
		(newValue: string, version: Guid): void => {
			setSearchTerm({
				value: newValue,
				version
			});
		},
		[]
	);

	const renderTabContent = (): ReactElement => {
		switch(activeView) {
			case AddressBookViewKind.Personal:
				return (
					<PersonalContactsTab
						searchTerm={searchTerm}
						onSearchTermChange={searchTermChangeHandler}
						onListItemClick={addAnalyticsEvent}
					/>
				);
			case AddressBookViewKind.Organization:
				return (
					<GlobalContactsTab
						searchTerm={searchTerm}
						onSearchTermChange={searchTermChangeHandler}
						onListItemClick={addAnalyticsEvent}
					/>
				);
			case AddressBookViewKind.Workspace:
				return (
					<WorkspaceContactsTab
						presenceUpdateInterval={liveUpdateInterval}
						searchTerm={searchTerm}
						onSearchTermChange={searchTermChangeHandler}
						onListItemClick={addAnalyticsEvent}
					/>
				);
			case AddressBookViewKind.Department:
				return (
					<WorkspaceContactsTab
						presenceUpdateInterval={liveUpdateInterval}
						searchTerm={searchTerm}
						onSearchTermChange={searchTermChangeHandler}
						onListItemClick={addAnalyticsEvent}
						department={department}
					/>
				);
			default:
				never(activeView);
		}
	};

	const tabChangeHandler = useCallback(
		(tabId: Guid): void => {
			for(const [viewKindKey, tabConfiguration] of Object.entries(tabConfigurationMap)) {
				if (tabConfiguration.tabId === tabId) {
					const viewKind: AddressBookViewKind = Number(viewKindKey) as AddressBookViewKind;
					setSelectedActiveView(viewKind);
					return;
				}
			}
		},
		[tabConfigurationMap]
	);

	const getNavigationText = (): string => {
		return activeTabConfiguration.tabName;
	};

	const navigationClickHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			const sourceFilter: SourceFilter | undefined = activeView === AddressBookViewKind.Department
				&& isNotNilOrWhiteSpace(department)
					? {
						kind: "department",
						value: department.trim()
					}
					: undefined;

			navigateToAddressBookPage(
				activeContactSource,
				sourceFilter
			);
		},
		[
			activeContactSource,
			activeView,
			addAnalyticsEvent,
			department
		]
	);

	const openNewContactDialogHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			setIsNewContactDialogOpen(true);
		},
		[addAnalyticsEvent]
	);

	const closeNewContactDialogHandler = useCallback(
		(): void => {
			setIsNewContactDialogOpen(false);
		},
		[]
	);

	const actionContext: ContactSource = activeContactSource;

	const actions = useMemo(
		(): readonly Action<ContactSource>[] => {
			return listDefaultActions({
				new: () => openNewContactDialogHandler()
			});
		},
		[openNewContactDialogHandler]
	);

	return (
		<>
			<LiveTileContent
				icon={icon}
				name={name}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
			>
				<LiveTileContent.Header
					icon={icon}
					actionContext={actionContext}
					actions={actions}
				>
					{tabs.length > 1
						? (
							<LiveTileContent.TabSelector
								tileId={id}
								tileName={name}
								value={activeTabId}
								onChange={tabChangeHandler}
								tabs={tabs}
							/>
						) : (
							<LiveTileContent.Title text={tabs[0].name} />
						)}
				</LiveTileContent.Header>
				<LiveTileContent.Content>

					<LiveTileContent.ContentStack>
						{renderTabContent()}
					</LiveTileContent.ContentStack>
				</LiveTileContent.Content>
				<LiveTileContent.Footer
					name={getNavigationText()}
					onClick={navigationClickHandler}
				/>
			</LiveTileContent>
			{isNewContactDialogOpen && (
				<ContactDialog
					initialView={{kind: "create"}}
					onClose={closeNewContactDialogHandler}
					source={ContactSource.Personal}
				/>
			)}
		</>
	);
};
AddressBookLiveTileContent.displayName = "AddressBookLiveTileContent";

type TabConfiguration = {
	tabId: Guid;
	contactSource: AddressBookContactSource;
	tabName: string;
};

type ActionHandlers = {
	new: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ContactSource>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-contact",
			title: localize("_NewContact"),
			icon: "plus",
			canExecute: (x) => x === ContactSource.Personal,
			execute: () => handlers.new()
		}
	];
}
